import { useState, useEffect } from "react"
import { motion, useScroll } from "motion/react"
import { Link } from "@inertiajs/react"
import { cn } from "@/lib/utils"

const NAV_LINKS = [
  { href: "#services", label: "Services" },
  { href: "#products", label: "Produits" },
  { href: "#clients", label: "Clients" },
  { href: "#contact", label: "Contact" },
]

// Real routed page (not an in-page anchor), so it uses Inertia navigation.
const CHRONO_FINISH_LINK = { href: "/chrono-finish", label: "Chalky Chrono Finish" }

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { scrollY } = useScroll()

  useEffect(() => {
    return scrollY.on("change", (y) => {
      setScrolled(y > 50)
    })
  }, [scrollY])

  const isHome = typeof window !== "undefined" && window.location.pathname === "/"

  const NAVBAR_HEIGHT = 64

  const scrollToElement = (href: string) => {
    const target = document.querySelector(href)
    if (target) {
      const top = target.getBoundingClientRect().top + window.scrollY - NAVBAR_HEIGHT
      window.scrollTo({ top, behavior: "smooth" })
    }
  }

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!isHome) {
      window.location.href = `/${href}`
      return
    }
    e.preventDefault()
    scrollToElement(href)
    setMobileOpen(false)
  }

  return (
    <>
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
          scrolled
            ? "bg-[rgba(10,10,15,0.85)] backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20"
            : "bg-transparent"
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a
              href="/"
              className="text-xl font-bold text-white tracking-tight hover:text-violet-400 transition-colors"
              onClick={(e) => {
                if (isHome) {
                  e.preventDefault()
                  window.scrollTo({ top: 0, behavior: "smooth" })
                }
              }}
            >
              Chalky
            </a>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className="text-sm text-slate-300 hover:text-white transition-colors duration-200 relative group"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-violet-400 transition-all duration-300 group-hover:w-full" />
                </a>
              ))}
              <Link
                href={CHRONO_FINISH_LINK.href}
                className="text-sm text-slate-300 hover:text-white transition-colors duration-200 relative group"
              >
                {CHRONO_FINISH_LINK.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-violet-400 transition-all duration-300 group-hover:w-full" />
              </Link>
            </div>

            {/* CTA button */}
            <div className="hidden md:block">
              <a
                href="#contact"
                onClick={(e) => handleAnchorClick(e, "#contact")}
                className="px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors duration-200"
              >
                Démarrer un projet
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-white"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              <div className="w-5 flex flex-col gap-1.5">
                <span
                  className={cn(
                    "block h-px bg-current transition-all duration-300",
                    mobileOpen ? "rotate-45 translate-y-2" : ""
                  )}
                />
                <span
                  className={cn(
                    "block h-px bg-current transition-all duration-300",
                    mobileOpen ? "opacity-0" : ""
                  )}
                />
                <span
                  className={cn(
                    "block h-px bg-current transition-all duration-300",
                    mobileOpen ? "-rotate-45 -translate-y-2" : ""
                  )}
                />
              </div>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <motion.div
        initial={false}
        animate={{ opacity: mobileOpen ? 1 : 0, x: mobileOpen ? 0 : "100%" }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={cn(
          "fixed inset-y-0 right-0 z-40 w-64 bg-[rgba(10,10,15,0.95)] backdrop-blur-xl border-l border-white/10 flex flex-col pt-20 px-6 gap-6",
          !mobileOpen && "pointer-events-none"
        )}
      >
        {NAV_LINKS.map((link) => (
          <a
            key={link.href}
            href={link.href}
            onClick={(e) => handleAnchorClick(e, link.href)}
            className="text-lg text-slate-300 hover:text-white transition-colors"
          >
            {link.label}
          </a>
        ))}
        <Link
          href={CHRONO_FINISH_LINK.href}
          onClick={() => setMobileOpen(false)}
          className="text-lg text-slate-300 hover:text-white transition-colors"
        >
          {CHRONO_FINISH_LINK.label}
        </Link>
        <a
          href="#contact"
          onClick={(e) => handleAnchorClick(e, "#contact")}
          className="mt-4 px-4 py-3 text-center font-medium text-white bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors"
        >
          Démarrer un projet
        </a>
      </motion.div>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </>
  )
}
